import type { ListOpenDraftPurchaseOrdersResponse } from '@erp/rpc';

export type OpenDraftOrder =
  ListOpenDraftPurchaseOrdersResponse['orders'][number];

/** What a buyer calls the draft: its tracking number once sent, else a short id. */
export function openDraftLabel(order: OpenDraftOrder): string {
  return order.trackingNo ?? order.id.slice(0, 8).toUpperCase();
}

/**
 * The draft Release may add lines to: the most recent one that is approved, not
 * yet sent, and has no recorded approval decision. A draft awaiting approval
 * would have an in-flight approval restarted; a draft a gate already decided
 * would take the new lines on that earlier decision. `orders` arrive newest
 * first.
 */
export function pickAppendTarget(
  orders: readonly OpenDraftOrder[],
): OpenDraftOrder | null {
  return (
    orders.find(
      (order) => order.status === 'pending' && !order.hasApprovalDecision,
    ) ?? null
  );
}

/** The newest approved draft that cannot take lines because a gate decided it. */
export function pickDecidedDraft(
  orders: readonly OpenDraftOrder[],
): OpenDraftOrder | null {
  return (
    orders.find(
      (order) => order.status === 'pending' && order.hasApprovalDecision,
    ) ?? null
  );
}

/** The newest draft still awaiting approval, for the "not available" note. */
export function pickAwaitingApproval(
  orders: readonly OpenDraftOrder[],
): OpenDraftOrder | null {
  return orders.find((order) => order.status === 'pending_approval') ?? null;
}

/** What one Release sends: the in-cart lines, and the open PO to add them to. */
export interface ReleaseSelection {
  suggestionIds: string[];
  /** The open draft to add to, or null for a new PO. */
  appendToDraftPurchaseOrderId: string | null;
}
